import { motion } from "framer-motion";
import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const modules = [
  {
    num: "00",
    title: "Willkommen & Überblick",
    desc: "Ein Überblick über das gesamte System — wie es funktioniert, was dich erwartet und wie du von Anfang an den richtigen Fokus setzt.",
  },
  {
    num: "01",
    title: "Kunden finden",
    desc: "Lerne, wie du täglich lokale Betriebe identifizierst, die dringend eine neue Website brauchen — mit einer simplen Methode, die keine Vorerfahrung erfordert.",
  },
  {
    num: "02",
    title: "Kaltakquise am Telefon",
    desc: "Wie du Betriebe kurz und überzeugend anrufst und in 1–2 Minuten Interesse weckst. Inklusive bewährtem Original-Skript und Antworten auf die häufigsten Einwände.",
  },
  {
    num: "03",
    title: "Website bauen",
    desc: "Die eigentliche Magie: Eine vollständige, professionelle Website entsteht in unter 15 Minuten — mit einem einzigen Prompt. Live demonstriert, sofort nachmachbar.",
  },
  {
    num: "04",
    title: "Demo versenden",
    desc: "Wie du die fertige Website als kostenlose Preview an den Kunden sendest — so dass er sofort sieht was er bekommt, bevor er einen Euro zahlt.",
  },
  {
    num: "05",
    title: "Angebot & Preise",
    desc: "Das Cashflow-Modell: Wie du Einmalzahlungen mit monatlichen Einnahmen kombinierst und Pakete schnürst, die Kunden überzeugen.",
  },
  {
    num: "06",
    title: "Nachfassen & Abschluss",
    desc: "Wie du Interessenten gezielt nachfasst, letzte Einwände professionell räumst und den Auftrag verbindlich und sicher abschließt.",
  },
  {
    num: "07",
    title: "Live schalten, Domain & Abrechnung",
    desc: "Website live schalten, Domain für den Kunden einrichten und monatliche Abrechnung aufsetzen — für wiederkehrendes, passives Einkommen.",
  },
  {
    num: "08",
    title: "Skalieren",
    desc: "Der Premium-Bonus: Wie du das System auf mehrere gleichzeitige Aufträge skalierst und ein stabiles, wachsendes Einkommen aufbaust.",
    bonus: true,
  },
];

function ModuleRow({ mod, isOpen, onClick, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, ease, delay: index * 0.05 }}
      className="border-b border-white/6 last:border-0"
      data-testid={`module-item-${index}`}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`module-trigger-${index}`}
      >
        <div className="flex items-center gap-5 flex-1 min-w-0">
          <span className={`text-xs font-bold tracking-widest font-mono shrink-0 ${mod.bonus ? "text-[#E84500]/60" : "text-[#C9A84C]/40"}`}>
            {mod.bonus ? "BONUS" : `M${mod.num}`}
          </span>
          <span className="text-[#F5F5F7] font-semibold text-base sm:text-lg group-hover:text-[#C9A84C] transition-colors duration-200 leading-tight">
            {mod.title}
          </span>
        </div>
        <div className="shrink-0 ml-4 w-6 h-6 flex items-center justify-center">
          {isOpen ? (
            <Minus className="w-4 h-4 text-[#C9A84C]" />
          ) : (
            <Plus className="w-4 h-4 text-[#86868b] group-hover:text-[#C9A84C] transition-colors" />
          )}
        </div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-[#86868b] text-sm leading-relaxed pl-9">{mod.desc}</p>
      </motion.div>
    </motion.div>
  );
}

export default function ModulesSection() {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);

  return (
    <section
      className="py-32 md:py-40 px-6 bg-[#0A0A0A]"
      data-testid="modules-section"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-14"
        >
          <span className="text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase">
            Kursinhalt
          </span>
          <h2
            className="text-4xl sm:text-5xl font-black text-[#F5F5F7] mt-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}
          >
            9 Module. Vom Anfang
            <br />bis zum ersten Kunden.
          </h2>
          <p className="text-[#86868b] text-base mt-4">
            Kompaktes Video-Format · Direkt anwendbar · Lebenslanger Zugang
          </p>
        </motion.div>

        <div>
          {modules.map((mod, i) => (
            <ModuleRow
              key={i}
              mod={mod}
              index={i}
              isOpen={openIdx === i}
              onClick={() => toggle(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
